import './DispatchDesk.css';
import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
  useDeferredValue,
  memo,
} from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { makeIncidentId } from '../utils/ids';

// === local utils & components (use your real ones if you have them) ===
import { streamIncidents, archiveIncident, addIncident } from '../firebaseUtils';
import IncidentCard from '../units/IncidentCard';
import { jaccardSim } from "../utils/textSim";
import { verifyDuplicateWithCAD } from "../utils/verification";
import AffirmationBanner from '../components/AffirmationBanner';

// 🔽 your new preview panel
import LifeLinkAddOns from "../components/LifeLinkAddOns";

// ========= Helpers (unchanged logic) =========

// Urgency → colored letter marker (C/M/L)
// OPTIMIZATION: cache generated divIcons (Leaflet objects) per urgency
const _iconCache = new Map();
const getUrgencyDivIcon = (urgency) => {
  if (_iconCache.has(urgency)) return _iconCache.get(urgency);
  const colorMap = { Critical: '#dc3545', Moderate: '#ffc107', Low: '#28a745' };
  const letterMap = { Critical: 'C', Moderate: 'M', Low: 'L' };
  const icon = L.divIcon({
    className: 'custom-div-icon',
    html: `<div style="
      background-color:${colorMap[urgency] || '#666'};
      color:#fff;font-weight:700;border-radius:50%;
      width:28px;height:28px;display:flex;align-items:center;justify-content:center;
      font-size:14px;border:2px solid #fff;box-shadow:0 0 4px rgba(0,0,0,.3);
    ">${letterMap[urgency] || '?'}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -15],
  });
  _iconCache.set(urgency, icon);
  return icon;
};

// Normalize dispatch to lowercase string(s) safely
const toWords = (d) => {
  if (!d) return '';
  if (Array.isArray(d)) return d.map((x) => (typeof x === 'string' ? x : String(x))).join(' ').toLowerCase();
  if (typeof d === 'string') return d.toLowerCase();
  if (typeof d === 'object') {
    const label = d.label || d.name;
    return label ? String(label).toLowerCase() : String(d).toLowerCase();
  }
  return String(d).toLowerCase();
};

const unitEmojiSafe = (d) => {
  const v = toWords(d);
  if (v.includes('police') || v.includes('law')) return '🚓';
  if (v.includes('ambulance') || v.includes('emt') || v.includes('medic') || v.includes('paramedic')) return '🚑';
  if (v.includes('fire') || v.includes('rescue')) return '🚒';
  return '🚨';
};

const getDisplayCode = (incident) => {
  if (incident?.incidentCode) return incident.incidentCode;
  if (incident?.code) return incident.code;
  if (incident?.id) return incident.id.slice(-6).toUpperCase();
  return 'UNKNOWN';
};

/* ========= Smart dispatch routing (unchanged) ========= */
const ROUTING_RULES = [
  { rx: /(overdose|unconscious|faint(ing)?|not breathing|cardiac|heart attack|seizure|stroke|narcan|naloxone)/i, add: ['EMS'] },
  { rx: /(house fire|structure fire|apartment fire|kitchen fire|smoke|flames|wildfire|brush fire|alarm(?!.*car))/i, add: ['Fire'] },
  { rx: /(vehicle crash|mvc|mva|collision|hit[- ]and[- ]run|fender bender|pile[- ]?up|car accident|rollover)/i, add: ['Police','EMS'] },
  { rx: /(active shooter|shots? fired|gun(shot)?|weapon|armed|stabbing|assault|robbery|home invasion|hostage|kidnap)/i, add: ['Police','EMS'] },
  { rx: /(suspicious package|bomb|explosion|device)/i, add: ['Police','Fire'] },
  { rx: /(disturbance|disorderly|noise complaint|loud party|crowd|riot|bar fight)/i, add: ['Police'] },
  { rx: /(missing child|missing person|welfare check|wellness check|elderly missing)/i, add: ['Police'] },
  { rx: /(burglary alarm|security alarm|panic alarm|hold[- ]up alarm)/i, add: ['Police'] },
];

function routeUnitsByKeywords(incident) {
  const text = [
    incident?.title,
    incident?.notes,
    incident?.description,
    incident?.details,
  ].filter(Boolean).join(' ').toLowerCase();

  const set = new Set();
  ROUTING_RULES.forEach(rule => {
    if (rule.rx.test(text)) rule.add.forEach(u => set.add(u));
  });

  if (set.size === 0) {
    switch (incident?.urgency) {
      case 'Critical': set.add('Police'); set.add('EMS'); break;
      case 'Moderate': set.add('Police'); break;
      default: set.add('Police'); break;
    }
  }
  return Array.from(set);
}

function getDispatchUnits(incident) {
  if (Array.isArray(incident?.dispatch)) return incident.dispatch;
  if (typeof incident?.dispatch === 'string' && incident.dispatch.trim()) return [incident.dispatch];
  return routeUnitsByKeywords(incident);
}

// Simple titles for simulator
const randomIncidentTitles = [
  'Armed Robbery Reported',
  'House Fire in Progress',
  'Missing Child Alert',
  'Vehicle Crash on I-40',
  'Active Shooter Warning',
  'Overdose Emergency',
  'Flooded Roadway Warning',
  'Suspicious Package',
  'Fire Alarm Triggered',
  'Medical Emergency – Fainting',
  'Large Crowd Gathering',
];

// Haversine (km)
const toRad = (deg) => (deg * Math.PI) / 180;
const haversineKm = (a, b) => {
  if (!a || !b) return Infinity;
  const R = 6371;
  const dLat = toRad((b.lat ?? 0) - (a.lat ?? 0));
  const dLng = toRad((b.lng ?? 0) - (a.lng ?? 0));
  const lat1 = toRad(a.lat ?? 0);
  const lat2 = toRad(b.lat ?? 0);
  const sin1 = Math.sin(dLat / 2);
  const sin2 = Math.sin(dLng / 2);
  const c = sin1 * sin1 + Math.cos(lat1) * Math.cos(lat2) * sin2 * sin2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(c)));
};

// GEO + TIME + KEYWORDS (+ optional CAD verification)
const classifyDuplicateAsync = async (next, previousList) => {
  const now = Date.now();
  let best = null;
  let bestDist = Infinity;

  previousList.forEach((i) => {
    const ts = new Date(i.timestamp || i.createdAt?.seconds * 1000 || now).getTime();
    const ageMs = now - ts;
    if (ageMs > 6 * 60 * 1000) return; // last ~6 min

    const distKm = haversineKm(
      { lat: i.location?.lat, lng: i.location?.lng },
      { lat: next.location?.lat, lng: next.location?.lng }
    );
    if (distKm < bestDist) { bestDist = distKm; best = i; }
  });

  if (!best || !isFinite(bestDist)) return { match: null, status: 'none' };

  const sim = jaccardSim(next.title, best.title);
  const SIM_THRESHOLD = 0.45;
  const NEAR_KM = 1.0;
  const NEAR_TIME_MS = 3 * 60 * 1000;

  const dt = new Date(next.timestamp).getTime() - new Date(best.timestamp).getTime();

  if (sim < SIM_THRESHOLD) return { match: null, status: 'none' };

  if (bestDist <= NEAR_KM && Math.abs(dt) <= NEAR_TIME_MS) {
    const ok = await verifyDuplicateWithCAD(next, best);
    return { match: best, status: ok ? 'confirmed' : 'possible' };
  }

  if (bestDist <= 3) return { match: best, status: 'possible' };

  return { match: null, status: 'none' };
};

/* ========= Duplicate simulation helpers ========= */
const DUP_WINDOW_MIN = 6;
const DUP_PROBABILITY = 0.30;
const DUP_JITTER_DEG = 0.02;

const pickRecentIncident = (list) => {
  const now = Date.now();
  const recent = list.filter((i) => {
    const ts = new Date(i.timestamp || i.createdAt?.seconds * 1000 || now).getTime();
    return (now - ts) <= DUP_WINDOW_MIN * 60 * 1000;
  });
  if (recent.length === 0) return null;
  return recent[Math.floor(Math.random() * recent.length)];
};

// Firestore-safe: remove all undefined values
const deepClean = (val) => {
  if (Array.isArray(val)) return val.map(deepClean).filter((v) => v !== undefined);
  if (val && typeof val === 'object') {
    const out = {};
    Object.entries(val).forEach(([k, v]) => {
      const nv = deepClean(v);
      if (nv !== undefined) out[k] = nv;
    });
    return out;
  }
  return val === undefined ? undefined : val;
};

/* ========= Queue helpers ========= */
const aiEligibleScore = (i) => {
  const title = (i.title || "").toLowerCase();
  const dispatchStr = Array.isArray(i.dispatch)
    ? i.dispatch.join(" ").toLowerCase()
    : (i.dispatch || "").toLowerCase();

  const unitsArr = getDispatchUnits(i);

  const positive = /(overdose|faint|medical|wellness|welfare|non-?emergency|alarm|fender|minor|noise|disturbance|lost|missing|crowd|package|suspicious|fire alarm)/;
  const negative = /(active shooter|armed|weapon|shots? fired|hostage|kidnap|terror|explosion|structure fire|multi-vehicle|mass casualty|pursuit)/;

  let score = 0;
  if (positive.test(title)) score += 2;
  if (/medical|emt|paramedic/.test(dispatchStr)) score += 1;
  if (negative.test(title)) score -= 5;

  if (unitsArr.includes('Fire') && !unitsArr.includes('EMS')) score -= 2;
  if (unitsArr.includes('Police') && unitsArr.includes('EMS')) score -= 1;

  if (i.urgency === "Low") score += 2;
  if (i.urgency === "Moderate") score += 1;
  if (i.urgency === "Critical") score -= 2;

  return score;
};

const fifoKey = (i) => new Date(i.timestamp || Date.now()).getTime();

const aiFirstPriority = (i) => {
  const uw = { Critical: 300, Moderate: 200, Low: 100 }[i.urgency] || 0;
  const ai = aiEligibleScore(i) * 20;
  const age = Date.now() - fifoKey(i);
  const backlog = (i.backlogCount || 0) * 50;
  return uw + ai + Math.min(age / 1000, 600) + backlog;
};

/* ========= Breather timing ========= */
const BREATHER_SECONDS = 30;
const AUTO_BREATHER_EVERY = 911;

/* ========= Component ========= */

export default function DispatchDesk() {
  const [incidents, setIncidents] = useState([]);
  const [filter, setFilter] = useState('All');
  const [liveFeed, setLiveFeed] = useState(true);
  const [incidentCounter, setIncidentCounter] = useState(0);
  const [queueMode, setQueueMode] = useState('severity'); // 'severity' | 'fifo' | 'aifirst'
  const [viewMode, setViewMode] = useState('compact');    // 'compact' | 'full'

  const DEFAULT_LIMITS = { Critical: 6, Moderate: 6, Low: 6 };
  const [laneExpanded, setLaneExpanded] = useState({ Critical: false, Moderate: false, Low: false });

  const [pauseUntil, setPauseUntil] = useState(0);
  const [nextAutoBreatherAt, setNextAutoBreatherAt] = useState(Date.now() + AUTO_BREATHER_EVERY * 1000);

  // OPTIMIZATION: refs to avoid stale closures & re-registering intervals
  const autoRef = useRef(null);
  const aiWorkerRef = useRef(null);
  const activeIncidentsRef = useRef([]);

  const [selectedIncident, setSelectedIncident] = useState(null);
  const openDetails = useCallback((incident) => setSelectedIncident(incident), []);
  const closeDetails = useCallback(() => setSelectedIncident(null), []);

  const isPaused = Date.now() < pauseUntil;
  const pauseRemaining = Math.max(0, Math.ceil((pauseUntil - Date.now()) / 1000));

  // Live stream
  useEffect(() => {
    const unsubscribe = streamIncidents((data) => setIncidents(data.reverse()));
    return () => unsubscribe();
  }, []);

  // Keep ref in sync for interval callbacks
  useEffect(() => {
    activeIncidentsRef.current = incidents.filter((i) => i.status !== 'archived');
  }, [incidents]);

  // ====== Derived data (memoized) ======
  const activeIncidents = useMemo(
    () => incidents.filter((i) => i.status !== 'archived'),
    [incidents]
  );

  const urgencyCounts = useMemo(
    () =>
      activeIncidents.reduce(
        (acc, i) => ({ ...acc, [i.urgency]: (acc[i.urgency] || 0) + 1 }),
        { Critical: 0, Moderate: 0, Low: 0 }
      ),
    [activeIncidents]
  );

  const heatmapSummary = useMemo(() => {
    const out = {};
    activeIncidents.forEach((i) => {
      const city = i?.location?.name || i?.location || 'Unknown';
      if (!out[city]) out[city] = { Critical: 0, Moderate: 0, Low: 0 };
      out[city][i.urgency] += 1;
    });
    return out;
  }, [activeIncidents]);

  // Sorters (unchanged logic)
  const sortIncidents = useCallback((list) => {
    const w = { Critical: 3, Moderate: 2, Low: 1 };
    return [...list].sort((a, b) => {
      const ua = w[a.urgency] || 0;
      const ub = w[b.urgency] || 0;
      if (ua !== ub) return ub - ua;
      const ba = a.backlogCount || 0;
      const bb = b.backlogCount || 0;
      if (ba !== bb) return bb - ba;
      const ta = new Date(a.timestamp).getTime();
      const tb = new Date(b.timestamp).getTime();
      return tb - ta;
    });
  }, []);

  const fifoSort = useCallback((list) => [...list].sort((a,b) => fifoKey(a) - fifoKey(b)), []);
  const aifirstSort = useCallback((list) => [...list].sort((a,b) => aiFirstPriority(b) - aiFirstPriority(a)), []);

  const sortIncidentsByQueueMode = useCallback((list) => {
    if (queueMode === 'fifo') return fifoSort(list);
    if (queueMode === 'aifirst') return aifirstSort(list);
    return sortIncidents(list);
  }, [queueMode, fifoSort, aifirstSort, sortIncidents]);

  const baseFiltered = useMemo(
    () => (filter === 'All' ? activeIncidents : activeIncidents.filter((i) => i.urgency === filter)),
    [activeIncidents, filter]
  );

  const sortedFiltered = useMemo(
    () => sortIncidentsByQueueMode(baseFiltered),
    [baseFiltered, sortIncidentsByQueueMode]
  );

  // OPTIMIZATION: de-prioritize heavy updates during bursts
  const filteredIncidents = useDeferredValue(sortedFiltered);

  // ====== Simulator (unchanged behavior; stabilized timer) ======
  const simTimerRef = useRef(null);
  useEffect(() => {
    if (!liveFeed) {
      if (simTimerRef.current) { clearInterval(simTimerRef.current); simTimerRef.current = null; }
      return;
    }
    simTimerRef.current = setInterval(() => simulateIncident(), 10000);
    return () => { if (simTimerRef.current) clearInterval(simTimerRef.current); };
    // only depends on liveFeed; simulateIncident reads from refs/state safely
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveFeed]);

  const startBreather = useCallback(() => {
    const until = Date.now() + BREATHER_SECONDS * 1000;
    setPauseUntil(until);
    setNextAutoBreatherAt(until + AUTO_BREATHER_EVERY * 1000);
  }, []);

  // Auto-breather every 911 seconds (stable)
  useEffect(() => {
    if (autoRef.current) clearInterval(autoRef.current);
    autoRef.current = setInterval(() => {
      const now = Date.now();
      if (now >= nextAutoBreatherAt && Date.now() >= pauseUntil) startBreather();
    }, 1000);
    return () => clearInterval(autoRef.current);
  }, [nextAutoBreatherAt, pauseUntil, startBreather]);

  // While paused, let AI auto-handle Low/Moderate, FIFO (1 every 5s)
  useEffect(() => {
    if (!isPaused) {
      if (aiWorkerRef.current) clearInterval(aiWorkerRef.current);
      return;
    }
    aiWorkerRef.current = setInterval(async () => {
      try {
        const candidates = activeIncidentsRef.current
          .filter(i => i.status !== 'archived' && (i.urgency === 'Low' || i.urgency === 'Moderate'))
          .sort((a,b) => fifoKey(a) - fifoKey(b));
        const target = candidates.find(i => (i.aiEligible ?? (aiEligibleScore(i) >= 2)));
        if (target) await archiveIncident(target);
      } catch (e) {
        console.error('AI auto-handle failed:', e);
      }
    }, 5000);
    return () => clearInterval(aiWorkerRef.current);
  }, [isPaused]);

 // ===== simulateIncident (same logic, IIFE-wrapped awaits) =====
const simulateIncident = useCallback(async () => {
  const urgencies = ["Critical", "Moderate", "Low"];
  const randomUrgency = urgencies[Math.floor(Math.random() * urgencies.length)];
  const randomTitle = randomIncidentTitles[Math.floor(Math.random() * randomIncidentTitles.length)];

  const baseLocation = {
    name: "Simulated City",
    lat: 35.7796 + (Math.random() - 0.5) * 0.5,
    lng: -78.6382 + (Math.random() - 0.5) * 0.5,
  };

  let payload = {
    title: randomTitle,
    type: "Simulation",
    urgency: randomUrgency,
    dispatch: routeUnitsByKeywords({ title: randomTitle, urgency: randomUrgency }),
    location: baseLocation,
    timestamp: new Date().toISOString(),
    duplicateStatus: "none",
  };

  const cityName = typeof payload.location === "object"
    ? (payload.location.name || "City")
    : (payload.location || "City");
  payload.id = makeIncidentId(
    cityName,
    payload.location?.lat,
    payload.location?.lng,
    Date.now()
  );

  if (Math.random() < DUP_PROBABILITY) {
    const candidate = pickRecentIncident(activeIncidentsRef.current);
    if (candidate) {
      const jitter = () => (Math.random() - 0.5) * DUP_JITTER_DEG;
      const dupCandidate = {
        title: candidate.title,
        type: candidate.type || "Simulation",
        urgency: "Low",
        dispatch: Array.isArray(candidate.dispatch) && candidate.dispatch.length
          ? candidate.dispatch
          : routeUnitsByKeywords({ title: candidate.title, urgency: "Low" }),
        location: {
          name: typeof candidate.location === "object"
            ? candidate.location.name || "Simulated City"
            : candidate.location || "Simulated City",
          lat: (candidate.location?.lat ?? baseLocation.lat) + jitter(),
          lng: (candidate.location?.lng ?? baseLocation.lng) + jitter(),
        },
        timestamp: new Date().toISOString(),
        simulatedDuplicate: true,
      };

      // ✅ FIXED AWAIT BLOCK #1
      await (async () => {
        const { match, status } = await classifyDuplicateAsync(dupCandidate, activeIncidentsRef.current);
        payload = {
          ...dupCandidate,
          duplicateStatus: status,
          ...(match?.title ? { duplicateOfTitle: match.title } : {}),
        };
      })();
    }
  } else {
    // ✅ FIXED AWAIT BLOCK #2
    await (async () => {
      const { match, status } = await classifyDuplicateAsync(payload, activeIncidentsRef.current);
      payload.duplicateStatus = status;
      if (match?.title) payload.duplicateOfTitle = match.title;
    })();
  }

  payload.aiEligible = aiEligibleScore(payload) >= 2;

  const safe = deepClean(payload);
  addIncident(safe);
  setIncidentCounter((n) => n + 1);
}, []);

  const handleAccept = useCallback(async (incident) => {
    if (isPaused) return;
    try {
      await archiveIncident(incident);
      if (selectedIncident?.id === incident.id) closeDetails();
    } catch (e) {
      console.error('Failed to archive:', e);
    }
  }, [isPaused, selectedIncident, closeDetails]);

  // ===== UI pieces

  // OPTIMIZATION: memoized CompactCard to avoid re-render storms
  const CompactCard = memo(({ incident }) => {
    const urgency = incident?.urgency || 'Low';
    const units = getDispatchUnits(incident);
    const emoji = (units.length ? units : ['']).map(unitEmojiSafe).join(' ') || '🚨';
    const dup = incident.duplicateStatus && incident.duplicateStatus !== 'none';
    const code = getDisplayCode(incident);

    return (
      <button
        className="compact-card"
        onClick={() => openDetails(incident)}
        title="Open details"
        style={{
          textAlign: 'left',
          border: '1px solid #e5e7eb',
          borderRadius: 10,
          padding: '8px 10px',
          background: '#fff',
          cursor: 'pointer',
          display: 'grid',
          gridTemplateRows: 'auto auto',
          gap: 6,
          minWidth: 0
        }}
      >
        <div style={{display:'flex', alignItems:'center', gap:8, minWidth:0}}>
          <span
            className={`badge urgency-${urgency.toLowerCase()}`}
            style={{
              display:'inline-flex',
              alignItems:'center',
              justifyContent:'center',
              width:22, height:22, borderRadius:'50%',
              fontSize:12, fontWeight:700, color:'#fff',
              background: urgency==='Critical' ? '#dc3545' : urgency==='Moderate' ? '#f59e0b' : '#22c55e'
            }}
          >
            {urgency[0]}
          </span>
          <span
            className="incident-title"
            style={{
              fontWeight:600,
              fontSize:13,
              overflow:'hidden',
              textOverflow:'ellipsis',
              whiteSpace:'nowrap',
              flex:1
            }}
          >
            {incident.title}
          </span>
        </div>

        <div style={{fontSize:11, color:'#6b7280', marginTop:2}}>
          Auto dispatching: {(getDispatchUnits(incident).length ? getDispatchUnits(incident) : [''])
            .map(unitEmojiSafe).join(' ')}
        </div>

        <div style={{display:'flex', alignItems:'center', gap:8, flexWrap:'nowrap', minWidth:0}}>
          <span style={{fontSize:12, color:'#6b7280'}}>#{code}</span>
          <span aria-hidden style={{marginLeft:'auto'}}>{emoji}</span>
          <span style={{display:'flex', gap:4, marginLeft:4}}>
            {units.map(u => (
              <span key={u} style={{
                fontSize:10, padding:'1px 4px', border:'1px solid #e5e7eb',
                borderRadius:6, background:'#f9fafb', color:'#111827'
              }}>{u}</span>
            ))}
          </span>
          {incident.aiEligible && <span className="ai-badge" style={{fontSize:12}}>🤖</span>}
          {dup && <span className="dup-badge" style={{fontSize:10, background:'#eef2ff', border:'1px solid #c7d2fe', color:'#1e40af', padding:'2px 6px', borderRadius:6}}>DUP</span>}
        </div>
      </button>
    );
  }, (prev, next) => {
    // Only re-render if these change (keeps UI in sync but avoids churn)
    return (
      prev.incident.id === next.incident.id &&
      prev.incident.status === next.incident.status &&
      prev.incident.urgency === next.incident.urgency &&
      prev.incident.title === next.incident.title &&
      prev.incident.duplicateStatus === next.incident.duplicateStatus &&
      prev.incident.aiEligible === next.incident.aiEligible &&
      String(prev.incident.dispatch) === String(next.incident.dispatch)
    );
  });

  const limitBucketForLane = (level, bucket) => {
    const limit = DEFAULT_LIMITS[level] ?? 6;
    if (laneExpanded[level]) return bucket;
    return bucket.slice(0, limit);
  };

  const remainingCount = (level, bucketAll) => {
    const limit = DEFAULT_LIMITS[level] ?? 6;
    return Math.max(0, bucketAll.length - limit);
  };

  // ===== Render
  return (
    <div className="dispatch-desk-wrapper">
      <AffirmationBanner />

      {/* Top banner + simulator + live toggle */}
      <div className="simulate-banner">
        <h2>You are the calm in someone’s chaos.</h2>
        <button className="simulate-button" onClick={simulateIncident} disabled={isPaused} title={isPaused ? `Breather active (${pauseRemaining}s)` : 'Simulate a call'}>
          + Simulate Incoming 911 Call
        </button>
        <div className="live-toggle">
          <label><strong>Live 911 Feed:</strong></label>
          <button onClick={() => setLiveFeed(v => !v)} style={{ marginLeft: '.5rem' }}>
            {liveFeed ? '✅ YES' : '❌ NO'}
          </button>
        </div>
      </div>

      {/* Breather controls & status */}
      <div className="breather-row" style={{display:'flex', gap:12, alignItems:'center', margin:'0.5rem 0 0.75rem'}}>
        <button
          onClick={startBreather}
          style={{ padding:'6px 10px', borderRadius:8, border:'1px solid #e5e7eb', cursor:'pointer', background:isPaused ? '#111827' : '#fff', color: isPaused ? '#fff' : '#111827', fontWeight:600 }}
          title="Pause dispatcher intake for 30 seconds. AI will handle Low/Moderate FIFO."
        >
          {isPaused ? `⏸️ Breather (${pauseRemaining}s)` : '⏸️ Take 30s Breather'}
        </button>
        <div style={{fontSize:12, color:'#6b7280'}}>
          Auto-breather every <strong>911s</strong> • next at ~ {new Date(nextAutoBreatherAt).toLocaleTimeString()}
        </div>
        {isPaused && (
          <div style={{marginLeft:'auto', background:'#fff7ed', border:'1px solid #fdba74', color:'#9a3412', padding:'6px 10px', borderRadius:8}}>
            🤖 LifeLink AI is handling <strong>Low/Moderate</strong> calls FIFO while you breathe.
          </div>
        )}
      </div>

      {/* Filter + Queue + View controls */}
      <div className="incident-stats-wrapper" style={{display:'flex', gap:12, alignItems:'center', flexWrap:'wrap'}}>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {['All', 'Critical', 'Moderate', 'Low'].map((level) => (
            <button
              key={level}
              onClick={() => setFilter(level)}
              className={`urgency-button ${filter === level ? 'active' : ''}`}
            >
              {level}
            </button>
          ))}
        </div>

        <div className="queue-mode-controls" style={{display:'flex', gap:8, alignItems:'center', marginLeft:'auto', flexWrap:'wrap'}}>
          <div style={{fontWeight:600}}>Queue:</div>
          {[
            {id:'severity', label:'Severity'},
            {id:'fifo', label:'FIFO'},
            {id:'aifirst', label:'AI-First'},
          ].map(opt => (
            <button
              key={opt.id}
              onClick={() => setQueueMode(opt.id)}
              style={{
                padding:'6px 10px',
                borderRadius:8,
                border:'1px solid #e5e7eb',
                background: queueMode===opt.id ? '#111827' : '#fff',
                color: queueMode===opt.id ? '#fff' : '#111827',
                cursor:'pointer',
                fontSize:12,
                fontWeight:600
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div className="view-mode-controls" style={{display:'flex', gap:8, alignItems:'center', flexWrap:'wrap'}}>
          <div style={{fontWeight:600}}>View:</div>
          {[
            {id:'compact', label:'Compact Grid'},
            {id:'full', label:'Full Cards'},
          ].map(opt => (
            <button
              key={opt.id}
              onClick={() => setViewMode(opt.id)}
              style={{
                padding:'6px 10px',
                borderRadius:8,
                border:'1px solid #e5e7eb',
                background: viewMode===opt.id ? '#111827' : '#fff',
                color: viewMode===opt.id ? '#fff' : '#111827',
                cursor:'pointer',
                fontSize:12,
                fontWeight:600
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Kanban counters */}
      <div className="kanban-stats">
        {['Critical', 'Moderate', 'Low'].map((u) => (
          <div key={u} className={`kanban-card ${u.toLowerCase()}`}>
            <div className="kanban-title">{u}</div>
            <div className="kanban-count">{urgencyCounts[u]}</div>
          </div>
        ))}
      </div>

      {/* ===== Main + Sidebar layout ===== */}
      <div className="content-wrap">
        {/* Main column */}
        <div className="main-column">
          {/* Map */}
          <div style={{ height: '350px', marginBottom: '1.0rem' }}>
            <MapContainer
              center={[35.7796, -78.6382]}
              zoom={9}
              style={{ height: '100%', width: '100%', borderRadius: '12px' }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution="&copy; OpenStreetMap contributors"
              />
              {filteredIncidents.map((incident) => {
                const lat = incident?.location?.lat;
                const lng = incident?.location?.lng;
                const urgency = incident?.urgency;
                if (lat && lng && urgency) {
                  const icon = getUrgencyDivIcon(urgency);
                  const units = getDispatchUnits(incident);
                  const emoji = (units.length ? units : ['']).map(unitEmojiSafe).join(' ') || '🚨';

                  return (
                    <Marker key={incident.id} position={[lat, lng]} icon={icon}>
                      <Popup>
                        <div style={{ minWidth: 220 }}>
                          <div style={{ fontWeight: 700 }}>{incident.title}</div>
                          <div style={{ marginTop: 4 }}>
                            <strong>#{getDisplayCode(incident)}</strong> • <em>{incident.urgency}</em>
                          </div>
                          <div style={{ marginTop: 4 }}>
                            <strong>Dispatch:</strong> {emoji} {units.join(', ') || '—'}
                          </div>
                          <div style={{ marginTop: 8 }}>
                            <button
                              onClick={() => openDetails(incident)}
                              style={{
                                padding: '0.35rem 0.75rem',
                                backgroundColor: '#e9ecef',
                                color: '#212529',
                                border: 'none',
                                borderRadius: 4,
                                cursor: 'pointer',
                                fontWeight: 500,
                                fontSize: '0.85rem',
                              }}
                            >
                              View details
                            </button>
                          </div>
                        </div>
                      </Popup>
                    </Marker>
                  );
                }
                return null;
              })}
            </MapContainer>
          </div>

          {/* Incident lanes with per-lane limits + expanders */}
          <div className="incident-lanes">
            {['Critical', 'Moderate', 'Low'].map((level) => {
              const bucketAll = filteredIncidents.filter((i) => i.urgency === level);
              const bucket = limitBucketForLane(level, bucketAll);
              const remaining = remainingCount(level, bucketAll);

              return (
                <div key={level} className={`lane lane-${level.toLowerCase()}`}>
                  <h3 className="lane-title" style={{display:'flex', alignItems:'center', gap:8}}>
                    {level}
                    {isPaused && level !== 'Critical' && (
                      <span style={{fontSize:12, color:'#6b7280'}}>• AI assisting</span>
                    )}
                    {bucketAll.length > 0 && (
                      <span style={{marginLeft:'auto', fontSize:12, color:'#6b7280'}}>
                        Showing {bucket.length}{laneExpanded[level] ? '' : ` of ${bucketAll.length}`}
                      </span>
                    )}
                  </h3>

                  {/* Compact Grid */}
                  {viewMode === 'compact' && (
                    <div
                      className="lane-grid"
                      style={{
                        display:'grid',
                        gridTemplateColumns:'repeat(auto-fill, minmax(180px, 1fr))',
                        gap:8
                      }}
                    >
                      {bucket.map((incident) => (
                        <CompactCard key={incident.id} incident={incident} />
                      ))}
                      {bucket.length === 0 && (
                        <div className="lane-empty">No {level.toLowerCase()} calls</div>
                      )}
                    </div>
                  )}

                  {/* Full Cards */}
                  {viewMode === 'full' && (
                    <div className="lane-cards">
                      {bucket.map((incident) => (
                        <IncidentCard
                          key={incident.id}
                          incident={incident}
                          onAccept={isPaused ? () => {} : () => handleAccept(incident)}
                          onViewDetails={() => openDetails(incident)}
                        />
                      ))}
                      {bucket.length === 0 && <div className="lane-empty">No {level.toLowerCase()} calls</div>}
                    </div>
                  )}

                  {/* Lane expander */}
                  {remaining > 0 && !laneExpanded[level] && (
                    <div style={{marginTop:8}}>
                      <button
                        onClick={() => setLaneExpanded((s) => ({...s, [level]: true}))}
                        style={{padding:'6px 10px', border:'1px solid #e5e7eb', borderRadius:8, background:'#fff', cursor:'pointer', fontSize:12, fontWeight:600}}
                      >
                        + Show more ({remaining} more)
                      </button>
                    </div>
                  )}
                  {laneExpanded[level] && bucketAll.length > (DEFAULT_LIMITS[level] ?? 6) && (
                    <div style={{marginTop:8}}>
                      <button
                        onClick={() => setLaneExpanded((s) => ({...s, [level]: false}))}
                        style={{padding:'6px 10px', border:'1px solid #e5e7eb', borderRadius:8, background:'#fff', cursor:'pointer', fontSize:12, fontWeight:600}}
                      >
                        Show less
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Sidebar (Total + Heatmap summary) */}
        <aside className="sidebar">
          <div className="total-call-box">
            <h3>Total Calls: {activeIncidents.length}</h3>
            <div>🔴 <strong>Critical:</strong> {urgencyCounts.Critical}</div>
            <div>🟠 <strong>Moderate:</strong> {urgencyCounts.Moderate}</div>
            <div>🟢 <strong>Low:</strong> {urgencyCounts.Low}</div>
          </div>

          <div className="heatmap-summary-box compact">
            <h4>📍 Simulated Heatmap Summary</h4>
            <ul className="heatmap-list">
              {Object.entries(heatmapSummary).map(([city, levels]) => {
                let icon = '📍';
                if (levels.Critical > 0) icon = '🔥';
                else if (levels.Moderate > 0) icon = '🚗';
                else if (levels.Low > 0) icon = '🍂';

                const levelText = ['Critical', 'Moderate', 'Low']
                  .filter((lvl) => levels[lvl] > 0)
                  .map((lvl) => `${levels[lvl]} ${lvl}`)
                  .join(', ');

                return (
                  <li key={city}>
                    {icon} <strong>{city}</strong> – {levelText || 'No calls'}
                  </li>
                );
              })}
            </ul>
            {isPaused && (
              <div
                style={{
                  marginTop: 10,
                  background: '#eef2ff',
                  border: '1px solid #c7d2fe',
                  color: '#1e40af',
                  padding: '8px 10px',
                  borderRadius: 8
                }}
              >
                ⏱️ Breather in progress: <strong>{pauseRemaining}s</strong> left. AI is auto-handling Low/Moderate FIFO.
              </div>
            )}
          </div>
        </aside>
      </div>
      {/* ===== End Main + Sidebar ===== */}

      {/* ===== LifeLink Add-Ons (Preview) ===== */}
      <div style={{ marginTop: '1rem' }}>
        <LifeLinkAddOns />
      </div>

      {/* ===== Details Modal ===== */}
      {selectedIncident && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={closeDetails}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              width: 'min(720px, 92vw)',
              maxHeight: '85vh',
              overflowY: 'auto',
              borderRadius: 12,
              boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
              padding: '1rem 1.25rem',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 12 }}>
              <div>
                <h3 style={{ margin: 0 }}>{selectedIncident.title}</h3>
                <div style={{ color: '#666', marginTop: 4 }}>
                  #{getDisplayCode(selectedIncident)} • {selectedIncident.urgency}
                </div>
              </div>
              <button
                onClick={closeDetails}
                style={{
                  border: 'none',
                  background: '#eee',
                  borderRadius: 8,
                  padding: '0.25rem 0.6rem',
                  cursor: 'pointer',
                }}
              >
                ✖
              </button>
            </div>

            <div style={{ marginTop: 12, lineHeight: 1.6 }}>
              <div>
                <strong>Location:</strong>{' '}
                {typeof selectedIncident.location === 'object'
                  ? selectedIncident.location.name || 'Unknown'
                  : selectedIncident.location || 'Unknown'}
              </div>
              <div style={{ marginTop: 6 }}>
                <strong>Dispatch:</strong>{' '}
                {(() => {
                  const units = getDispatchUnits(selectedIncident);
                  const emoji = (units.length ? units : ['']).map(unitEmojiSafe).join(' ') || '🚨';
                  return `${emoji} ${units.join(', ') || '—'}`;
                })()}
              </div>
              {selectedIncident.aiEligible && (
                <div style={{ marginTop: 6, background: '#e6f7ff', padding: '6px 8px', borderRadius: 6 }}>
                  🤖 <strong>AI-Eligible</strong>
                </div>
              )}
              {selectedIncident.duplicateStatus && selectedIncident.duplicateStatus !== 'none' && (
                <div style={{ marginTop: 6, background: '#f3f4f6', padding: '6px 8px', borderRadius: 6 }}>
                  <strong>
                    {selectedIncident.duplicateStatus === 'confirmed' ? 'Duplicate Call' : 'Possible Duplicate'}
                  </strong>
                  {selectedIncident.duplicateOfTitle ? ` — related to: ${selectedIncident.duplicateOfTitle}` : ''}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button
                onClick={closeDetails}
                style={{
                  padding: '0.45rem 0.9rem',
                  backgroundColor: '#e9ecef',
                  color: '#212529',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontWeight: 500,
                }}
              >
                Close
              </button>

              {selectedIncident.status !== 'archived' && (
                <button
                  onClick={() => !isPaused && handleAccept(selectedIncident)}
                  disabled={isPaused}
                  style={{
                    padding: '0.45rem 0.9rem',
                    backgroundColor: isPaused ? '#9ca3af' : '#007bff',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 6,
                    cursor: isPaused ? 'not-allowed' : 'pointer',
                    fontWeight: 600,
                  }}
                  title={isPaused ? `Breather active (${pauseRemaining}s)` : 'Accept & Archive'}
                >
                  Accept & Archive
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {/* ===== End Details Modal ===== */}
    </div>
  );
}
